package diana.soleil.design.iraniancanadienbazzar.core.data

import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.model.AuthUser
import kotlinx.coroutines.flow.Flow

/** The account-level preference mirror stored on `users/{uid}` (all fields optional on the wire). */
data class UserPreferences(
    val preferredLanguage: String?,
    val metroId: String?,
    val savedBusinessIds: List<String>?,
    /** Bookmarked news article ids — the account mirror of the device-local reading list. */
    val savedNewsIds: List<String>? = null,
    /** Interest category ids picked in onboarding/Profile (personalizes Home ordering). */
    val interests: List<String>? = null,
)

/** The user's editable public profile on `users/{uid}` — shown on the Profile screen. */
data class UserProfile(
    val displayName: String?,
    val bio: String?,
    /** Epoch millis the account doc was created ("member since"), or null on legacy/absent docs. */
    val createdAtMillis: Long?,
    /** Whether the account opts in to push notifications (default on; read server-side by Functions). */
    val pushEnabled: Boolean = true,
    /**
     * Whether the account wants the once-a-day Hidden Gem push (default on). Independent of
     * [pushEnabled] on purpose: a daily unsolicited push is the one people most want to mute, and
     * without its own switch the only way to stop it would be killing order and chat pushes too.
     * `dailyGemPush` skips a user whose value is false.
     */
    val gemPushEnabled: Boolean = true,
    /**
     * CASL express consent for marketing emails (default OFF — Canadian anti-spam law forbids
     * pre-checked consent). Only consented users are targeted by the email campaign channel.
     */
    val emailMarketingConsented: Boolean = false,
    /** Mahaleh Pro (merchant subscription) active for this account. Server-set via the Stripe webhook. */
    val proActive: Boolean = false,
    /** The active plan id (`pro_monthly`/`pro_annual`), or null when not subscribed. */
    val proPlan: String? = null,
    /** Current billing-period end (epoch millis) — "renews/expires on". */
    val proUntilMillis: Long? = null,
)

/**
 * The signed-in user's own `users/{uid}` document (see `docs/DATA_MODEL.md`). Firestore rules only let
 * a user read/write their own doc, and `role` is locked to `'user'` on create and immutable to the
 * client afterwards — so this repository never touches role beyond the forced create value.
 */
interface UserProfileRepository {
    /**
     * Reads the account's stored preferences, or `null` when the user has no `users/{uid}` doc yet
     * (first sign-in). Lets a fresh device adopt the account's cross-device language/metro choices.
     */
    suspend fun getPreferences(uid: String): DataResult<UserPreferences?>

    /**
     * Reads the account's role (`user`/`business`/`admin`), or `null` when unset/no doc. UI-gating
     * only — Firestore rules are the real enforcement for every admin operation.
     */
    suspend fun getRole(uid: String): DataResult<String?>
    /**
     * Mirrors the local language + metro + saved-business choices onto `users/{uid}` so they follow
     * the account across devices. Creates the doc (role `'user'`) on first sign-in; on later calls
     * merge-writes only the preference fields. Best-effort — callers may ignore failures (the choices
     * stay cached locally either way).
     */
    suspend fun syncPreferences(
        user: AuthUser,
        preferredLanguage: String,
        metroId: String?,
        savedBusinessIds: Set<String>,
        savedNewsIds: Set<String> = emptySet(),
        interests: Set<String> = emptySet(),
    ): DataResult<Unit>

    /** Streams the signed-in user's editable profile (name/bio/created), null when signed out/no doc. */
    fun observeProfile(): Flow<DataResult<UserProfile?>>

    /** Merge-writes the signed-in user's display name and bio (role/preferences untouched). */
    suspend fun updateProfile(displayName: String, bio: String): DataResult<Unit>

    /**
     * Merge-writes the push-notification opt-in onto `users/{uid}.pushEnabled`. The Cloud Functions
     * that fan out retention pushes (e.g. onEventApproved) skip a user whose `pushEnabled` is false.
     */
    suspend fun setPushEnabled(enabled: Boolean): DataResult<Unit>

    /**
     * Merge-writes the daily-gem opt-in onto `users/{uid}.gemPushEnabled`. Read server-side by the
     * `dailyGemPush` scheduled Function, which also honours [setPushEnabled].
     */
    suspend fun setGemPushEnabled(enabled: Boolean): DataResult<Unit>

    /**
     * Records CASL express consent (or its withdrawal) for marketing emails onto
     * `users/{uid}.emailMarketing` — `{consented, updatedAt (server), source}` is the
     * proof-of-consent record; `sendCampaign`'s email channel only targets consented users.
     */
    suspend fun setEmailMarketingConsent(consented: Boolean): DataResult<Unit>

    /**
     * Starts a Mahaleh Pro subscription purchase ([planId] = `pro_monthly`/`pro_annual`) and returns
     * the Stripe hosted-Checkout URL to open. The `stripeWebhook` Function (not the client) grants the
     * `pro` entitlement after payment. Requires a real (non-anonymous) account.
     */
    suspend fun createSubscriptionCheckout(planId: String): DataResult<String>

    /**
     * Opens the Stripe Billing Portal (manage/cancel the subscription); returns its URL. Requires an
     * existing subscription (the webhook stamps the customer id onto the account first).
     */
    suspend fun createBillingPortalSession(): DataResult<String>
}
